import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  TrendingUp, Plus, Search, Upload, FileText, Image as ImageIcon, X, Trash2,
  Receipt, Calendar, CreditCard, Eye, Save, FileCheck, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { confirmAction } from "@/lib/confirm";

const CATEGORIES = [
  { value: "materiaux", label: "Matériaux chantier" },
  { value: "carburant", label: "Carburant" },
  { value: "outillage", label: "Outillage" },
  { value: "sous_traitance", label: "Sous-traitance" },
  { value: "loyer", label: "Loyer / dépôt" },
  { value: "assurances", label: "Assurances" },
  { value: "vehicules", label: "Véhicules" },
  { value: "telephone", label: "Téléphonie" },
  { value: "logiciels", label: "Logiciels" },
  { value: "comptable", label: "Expert-comptable" },
  { value: "banque", label: "Frais bancaires" },
  { value: "formation", label: "Formation" },
  { value: "marketing", label: "Marketing" },
  { value: "fournitures", label: "Fournitures bureau" },
  { value: "energie", label: "Énergie" },
  { value: "impots", label: "Impôts & taxes" },
  { value: "repas", label: "Repas / déplacement" },
  { value: "divers", label: "Divers" },
];

const PAY_METHODS = [
  { value: "cb", label: "Carte bancaire" },
  { value: "virement", label: "Virement" },
  { value: "especes", label: "Espèces" },
  { value: "cheque", label: "Chèque" },
  { value: "prelevement", label: "Prélèvement" },
];

const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(
    Number.isFinite(Number(n)) ? Number(n) : 0
  );

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Treasury() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [modal, setModal] = useState(null); // null | "new" | object
  const [importingFx, setImportingFx] = useState(false);

  const handleFacturxUpload = async (e) => {
    const file = e.target?.files?.[0];
    e.target.value = ""; // reset input pour réimport possible
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Merci de sélectionner un fichier PDF Factur-X.");
      return;
    }
    setImportingFx(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/expenses/import-facturx", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const p = data.parsed || {};
      // Pré-remplit un draft d'expense à partir du parsing
      const today = new Date().toISOString().slice(0, 10);
      const draft = {
        _prefilled: true,
        _source: "facturx",
        date: p.issue_date || today,
        category: "autres",
        supplier: p.supplier_name || "",
        supplier_siret: p.supplier_siret || "",
        description: [p.invoice_number, p.description].filter(Boolean).join(" — "),
        amount_ht: p.amount_ht || 0,
        vat_rate: p.dominant_vat_rate || 20,
        payment_method: "virement",
        status: "non_paye",
        payment_due_date: p.due_date || "",
        receipt_file: data.receipt_file || "",
        receipt_name: data.receipt_name || file.name,
        // Meta pour affichage
        _facturx_meta: {
          invoice_number: p.invoice_number,
          supplier_vat: p.supplier_vat,
          vat_breakdown: p.vat_breakdown || [],
          amount_ttc: p.amount_ttc,
        },
      };
      setModal(draft);
      toast.success(`Facture ${p.invoice_number || "Factur-X"} lue avec succès. Vérifiez puis enregistrez.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur d'import Factur-X.";
      toast.error(msg);
    } finally {
      setImportingFx(false);
    }
  };
  const [viewReceipt, setViewReceipt] = useState(null);

  const load = async () => {
    const { data } = await api.get("/expenses");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((e) => {
    if (filterCat && e.category !== filterCat) return false;
    if (!q) return true;
    const s = `${e.supplier || ""} ${e.description || ""} ${e.category}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const monthTotal = items
    .filter((e) => e.date?.slice(0, 7) === todayISO().slice(0, 7))
    .reduce((s, e) => s + (e.amount_ht || 0), 0);
  const yearTotal = items
    .filter((e) => e.date?.slice(0, 4) === todayISO().slice(0, 4))
    .reduce((s, e) => s + (e.amount_ht || 0), 0);

  const remove = async (id) => {
    if (!(await confirmAction("Supprimer cette dépense ?"))) return;
    await api.delete(`/expenses/${id}`);
    await load();
    toast.success("Dépense supprimée");
  };

  const openReceipt = async (id) => {
    const { data } = await api.get(`/expenses/${id}`);
    setViewReceipt(data);
  };

  return (
    <div data-testid="treasury-page" className="max-w-7xl mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-xs font-semibold text-[#FFC400] mb-3">
          <TrendingUp size={13} /> Dépenses
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">Dépenses</h1>
        <p className="text-secondary-text mt-3 max-w-3xl">
          Enregistrez chaque dépense (carburant, matériaux, abonnements…) avec son justificatif (photo ou PDF).
          Tout est récapitulé dans le tableau de bord.
        </p>
      </header>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiSmall label="Total ce mois HT" value={fmtEUR(monthTotal)} icon={Calendar} />
        <KpiSmall label="Total année HT" value={fmtEUR(yearTotal)} icon={TrendingUp} />
        <KpiSmall label="Dépenses enregistrées" value={items.length} icon={Receipt} />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary-text" />
            <input className="input pl-9 w-64" placeholder="Fournisseur, description..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="exp-search" />
          </div>
          <select className="input w-56" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="exp-cat-filter">
            <option value="">Toutes catégories</option>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="btn btn-outline cursor-pointer" data-testid="import-facturx-btn" style={{ minHeight: 40 }}>
            {importingFx ? (
              <><Loader2 size={15} className="animate-spin" /> Lecture Factur-X…</>
            ) : (
              <><FileCheck size={15} /> Importer Factur-X</>
            )}
            <input
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={handleFacturxUpload}
              disabled={importingFx}
              data-testid="import-facturx-input"
            />
          </label>
          <button onClick={() => setModal("new")} className="btn btn-accent" data-testid="add-expense">
            <Plus size={15} /> Nouvelle dépense
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-16 text-center text-tertiary-text">
            <Receipt size={36} className="mx-auto mb-3 opacity-40" />
            <div className="font-medium text-primary-text mb-1">Aucune dépense</div>
            <div className="text-sm">Cliquez sur « Nouvelle dépense » pour enregistrer votre premier ticket.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Fournisseur / Description</th>
                <th>Catégorie</th>
                <th>Paiement</th>
                <th>Justif.</th>
                <th className="text-right">Montant HT</th>
                <th className="text-right">TVA</th>
                <th className="text-right">Montant TTC</th>
                <th className="w-24 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="cursor-pointer" onClick={() => setModal(e)} data-testid={`exp-${e.id}`}>
                  <td className="text-sm font-mono">{e.date}</td>
                  <td>
                    <div className="font-medium">{e.supplier || "—"}</div>
                    <div className="text-xs text-tertiary-text">{e.description}</div>
                  </td>
                  <td><span className="badge badge-muted">{CATEGORIES.find((c) => c.value === e.category)?.label || e.category}</span></td>
                  <td className="text-sm text-secondary-text">{PAY_METHODS.find((p) => p.value === e.payment_method)?.label}</td>
                  <td>
                    {e.has_receipt ? (
                      <button onClick={(ev) => { ev.stopPropagation(); openReceipt(e.id); }} className="btn btn-ghost btn-sm" data-testid={`view-receipt-${e.id}`}>
                        <Eye size={13} />
                      </button>
                    ) : <span className="text-xs text-tertiary-text">—</span>}
                  </td>
                  <td className="text-right font-mono">{fmtEUR(e.amount_ht)}</td>
                  <td className="text-right text-sm text-secondary-text font-mono">{fmtEUR(e.vat_amount)}</td>
                  <td className="text-right font-mono font-semibold">{fmtEUR(e.amount_ttc)}</td>
                  <td className="text-center" onClick={(ev) => ev.stopPropagation()}>
                    <button
                      onClick={() => remove(e.id)}
                      className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-[rgba(225,6,0,0.35)] bg-[rgba(225,6,0,0.10)] text-[#E10600] hover:bg-[rgba(225,6,0,0.18)] hover:border-[#E10600] text-xs font-medium transition-colors"
                      title="Supprimer cette dépense"
                      data-testid={`exp-delete-${e.id}`}
                    >
                      <Trash2 size={13} /> Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <ExpenseModal
          item={modal === "new" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={async () => { await load(); setModal(null); }}
        />
      )}

      {viewReceipt && <ReceiptViewer expense={viewReceipt} onClose={() => setViewReceipt(null)} />}

      <div className="mt-6 rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4 text-xs text-tertiary-text flex items-start gap-3" data-testid="bank-future">
        <CreditCard size={16} className="text-[#FFC400] mt-0.5 shrink-0" />
        <div>
          <b className="text-primary-text">Connexion bancaire — à venir</b><br />
          Vous pourrez bientôt connecter votre compte bancaire pour un rapprochement automatique avec vos factures émises. Fonctionnalité optionnelle (au choix du client).
        </div>
      </div>
    </div>
  );
}

function KpiSmall({ label, value, icon: Icon }) {
  return (
    <div className="kpi">
      <div className="flex items-start justify-between gap-3">
        <div className="kpi-label">{label}</div>
        <Icon size={16} className="text-tertiary-text shrink-0" strokeWidth={1.6} />
      </div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}

function ExpenseModal({ item, onClose, onSaved }) {
  const blank = {
    date: todayISO(), category: "materiaux", supplier: "", description: "",
    amount_ht: "", vat_rate: 20, payment_method: "cb", status: "paye",
    payment_due_date: "",
    receipt_file: "", receipt_name: "",
  };
  const [form, setForm] = useState(item ? { ...item, amount_ht: String(item.amount_ht ?? "") } : blank);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [filePreview, setFilePreview] = useState(item?.has_receipt ? "(justificatif déjà uploadé)" : "");
  const [loadingReceipt, setLoadingReceipt] = useState(false);

  // Load receipt detail if editing
  useEffect(() => {
    if (item && item.has_receipt && !item.receipt_file) {
      setLoadingReceipt(true);
      api.get(`/expenses/${item.id}`).then(({ data }) => {
        setForm((f) => ({ ...f, receipt_file: data.receipt_file, receipt_name: data.receipt_name }));
        setFilePreview(data.receipt_name || "Justificatif");
        setLoadingReceipt(false);
      });
    }
    // eslint-disable-next-line
  }, []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm({ ...form, receipt_file: ev.target.result, receipt_name: file.name });
      setFilePreview(file.name);
      // Auto-trigger AI scan if it's an image
      if (file.type.startsWith("image/")) {
        runAiScan(ev.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const runAiScan = async (dataUri) => {
    setScanning(true);
    try {
      const { data } = await api.post("/expenses/scan-receipt", { image_data_uri: dataUri });
      setForm((f) => ({
        ...f,
        date: data.date || f.date,
        supplier: data.supplier || f.supplier,
        amount_ht: data.amount_ht ? String(data.amount_ht) : f.amount_ht,
        vat_rate: data.vat_rate || f.vat_rate,
        category: data.category || f.category,
        description: data.description || f.description,
      }));
      toast.success("Ticket analysé par IA — vérifiez les champs avant de sauvegarder");
    } catch (e) {
      toast.error("Scan IA impossible : " + (e?.response?.data?.detail || e.message));
    } finally {
      setScanning(false);
    }
  };

  const clearFile = () => {
    setForm({ ...form, receipt_file: "", receipt_name: "" });
    setFilePreview("");
  };

  const vat = (Number(form.amount_ht) || 0) * (Number(form.vat_rate) || 0) / 100;
  const ttc = (Number(form.amount_ht) || 0) + vat;

  const save = async () => {
    if (!Number(form.amount_ht)) { toast.error("Montant requis"); return; }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        category: form.category,
        supplier: form.supplier || "",
        description: form.description || "",
        amount_ht: Number(form.amount_ht),
        vat_rate: Number(form.vat_rate) || 0,
        payment_method: form.payment_method,
        status: form.status,
        receipt_file: form.receipt_file || "",
        receipt_name: form.receipt_name || "",
        payment_due_date: form.status === "a_payer" ? (form.payment_due_date || "") : "",
      };
      if (item?.id) await api.patch(`/expenses/${item.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(item?.id ? "Dépense mise à jour" : "Dépense enregistrée");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="expense-modal">
        <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <h3 className="font-semibold text-lg">
            {item?.id ? "Modifier la dépense" : (item?._source === "facturx" ? "Nouvelle dépense — Factur-X importée" : "Nouvelle dépense")}
          </h3>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={14} /></button>
        </div>
        {item?._source === "facturx" && (
          <div className="px-6 py-3 flex items-start gap-2" style={{ background: "rgba(255,196,0,0.06)", borderBottom: "1px solid rgba(255,196,0,0.25)" }} data-testid="facturx-banner">
            <FileCheck size={16} style={{ color: "var(--pit-yellow)", flexShrink: 0, marginTop: 2 }} />
            <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
              <b style={{ color: "var(--pit-yellow)" }}>Facture Factur-X reçue de {item.supplier || "fournisseur"}</b>
              {item._facturx_meta?.invoice_number && <span> — N° <b>{item._facturx_meta.invoice_number}</b></span>}
              {item._facturx_meta?.amount_ttc > 0 && <span> — TTC extrait : <b>{item._facturx_meta.amount_ttc.toFixed(2)} €</b></span>}
              <div className="mt-1" style={{ color: "var(--grey)" }}>Vérifiez les champs avant d&apos;enregistrer. Le PDF est conservé en pièce jointe.</div>
            </div>
          </div>
        )}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Date</label>
              <input type="date" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="exp-date" />
            </div>
            <div>
              <label className="label">Catégorie</label>
              <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="exp-category">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Fournisseur</label>
              <input className="input" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Ex: Total, Leroy Merlin..." data-testid="exp-supplier" />
            </div>
            <div>
              <label className="label">Mode de paiement</label>
              <select className="input" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                {PAY_METHODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Description</label>
            <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: Plein gazole véhicule chantier" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Montant HT</label>
              <input type="number" step="0.01" className="input text-right font-mono" value={form.amount_ht} onChange={(e) => setForm({ ...form, amount_ht: e.target.value })} data-testid="exp-amount" />
            </div>
            <div>
              <label className="label">TVA %</label>
              <select className="input" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}>
                {[0, 5.5, 10, 20].map((v) => <option key={v} value={v}>{v}%</option>)}
              </select>
            </div>
            <div>
              <label className="label">Montant TTC</label>
              <input className="input text-right font-mono bg-[var(--carbon-3)]" value={fmtEUR(ttc)} readOnly />
            </div>
          </div>

          {/* Receipt upload */}
          <div>
            <label className="label flex items-center gap-2">
              Justificatif (photo ou PDF)
              {scanning && <span className="text-[#FFC400] flex items-center gap-1 text-[10px] normal-case"><span className="spinner" style={{width:12,height:12,borderWidth:1.5}} /> Analyse IA en cours…</span>}
            </label>
            {!form.receipt_file ? (
              <label className="border-2 border-dashed border-[var(--border)] rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-[#FFC400] hover:bg-[#FFF5F2] transition-colors" data-testid="upload-area">
                <Upload size={20} className="text-tertiary-text mb-2" />
                <div className="text-sm text-secondary-text">Cliquez pour ajouter un fichier</div>
                <div className="text-xs text-tertiary-text mt-1">JPG/PNG (scan IA auto) ou PDF · max 5 Mo</div>
                <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleFile} data-testid="receipt-upload" />
              </label>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-[#FFF5F2] border border-[#FFC400]/30 rounded-lg">
                {form.receipt_file?.startsWith("data:image") ? (
                  <ImageIcon size={20} className="text-[#FFC400]" />
                ) : (
                  <FileText size={20} className="text-[#FFC400]" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{filePreview || "Justificatif"}</div>
                  <div className="text-xs text-tertiary-text">{loadingReceipt ? "Chargement..." : "Joint à la dépense"}</div>
                </div>
                {form.receipt_file?.startsWith("data:image") && !scanning && (
                  <button onClick={() => runAiScan(form.receipt_file)} className="btn btn-outline btn-sm" title="Re-scanner avec IA" data-testid="rescan-btn">
                    🤖 Re-scanner
                  </button>
                )}
                <button onClick={clearFile} className="btn btn-ghost btn-sm text-[#E10600]" data-testid="clear-receipt"><X size={14} /></button>
              </div>
            )}
          </div>

          <div>
            <label className="label">Statut</label>
            <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="exp-status">
              <option value="paye">Payée</option>
              <option value="a_payer">À payer plus tard</option>
            </select>
          </div>

          {form.status === "a_payer" && (
            <div className="rounded-lg border border-[rgba(255,196,0,0.35)] bg-[rgba(255,196,0,0.08)] p-4">
              <label className="label text-[#FFC400]">Date prévue de paiement *</label>
              <input type="date" className="input" value={form.payment_due_date}
                onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })}
                required data-testid="exp-due-date" />
              <div className="text-xs text-[#FFCF40] mt-2">
                Cette dépense sera intégrée à votre <b>trésorerie prévisionnelle</b> du tableau de bord.
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[var(--border)] flex justify-end gap-2 sticky bottom-0 bg-[var(--surface)]">
          <button onClick={onClose} className="btn btn-outline">Annuler</button>
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="exp-save">
            <Save size={14} /> {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceiptViewer({ expense, onClose }) {
  const isImage = expense.receipt_file?.startsWith("data:image");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="receipt-viewer">
      <div className="bg-[var(--surface)] rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="font-semibold">{expense.supplier || "Justificatif"}</div>
            <div className="text-xs text-tertiary-text">{expense.date} · {fmtEUR(expense.amount_ttc)}</div>
          </div>
          <button onClick={onClose} className="btn btn-ghost"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-auto bg-[var(--carbon-3)] flex items-center justify-center p-4">
          {isImage ? (
            <img src={expense.receipt_file} alt="Justificatif" className="max-w-full max-h-[70vh] object-contain rounded shadow" />
          ) : (
            <iframe title="PDF" src={expense.receipt_file} className="w-full h-[70vh] rounded bg-[var(--surface)]" />
          )}
        </div>
      </div>
    </div>
  );
}
